// src/annihilation/dungeons/sunkenFold.js
// ============================================================
//  ANNIHILATION — The Sunken Fold (Batch 6).
//  Source of truth: ANNIHILATION_WORLD_ATLAS Part 3 (the Fallen
//  Gate, difficulty 12–15), ANNIHILATION_SYSTEMS_SPEC Part 6
//  (the nine-slot Boss Template + the Phantom Matriarch worked
//  example — implemented here VERBATIM), enemyTaxonomy.js
//  (BOSSES.phantom_matriarch is the filled template; this file
//  consumes it, it never re-authors boss law).
//
//  THE FIRST DUNGEON. Two chambers under the rotted gate:
//    · The Descent — a spectral gauntlet. Phantom Prowlers
//      phase in and out (Spectral family law: untouchable while
//      phased). Clear them and the crypt door opens.
//    · The Crypt — circular arena, four pillars. HER.
//
//  BOSS LAWS HONORED (Spec Part 6, Telegraph Design Rules):
//  · Every attack telegraphed ≥0.8s with a visual tell.
//  · Every attack has a counter (dodge / pillar / lit tile).
//  · Familiarity law: the FIRST time an attack appears it deals
//    non-lethal (reduced) damage — the boss teaches you once.
//  · Phases are Anima-threshold triggered; the enrage timer is
//    the only clock.
//  · No chip tactics: going down resets the fight to full.
//
//  Party scaling (1–4) is Batch 17 (PartySystem + MMOSystem
//  reuse); this is the solo instance of the same fight.
//  Damage numbers on the boss def are dials (⚑ flagged);
//  wind-ups, thresholds, the hook, and the enrage are spec.
// ============================================================

import React, { useCallback, useEffect, useRef, useState } from "react";
import {
  usePlayerAnimation,
  MobileDpad,
  MobileActionPad,
} from "./PlayerEntity";
// Being continuity (July 16): one body everywhere — and the traveler
// walked into the crypt behind you. They always do.
import { AnnihilationPlayer, getBeingPalette } from "./BeingSprite";
// The visual makeover (July 19): every move has its own strike —
// yours by being, the prowlers' claws, and HER four attacks each
// resolving with their own visual. One registry: AbilityFX.js.
import { useAbilityFX, AbilityFXLayer } from "./AbilityFX";
import {
  usePlayerController,
  applyBleedStack,
  tickBleed,
  MAX_BLEED_STACKS,
} from "./PlayerController";
import SkillBar from "./SkillBar";
import {
  ENEMY_DEFS,
  buildEnemy,
  getBoss,
  xpForKill,
} from "./enemyTaxonomy";
import {
  deltaXp,
  deltaBloodMoney,
  recordDungeonClear,
} from "./profileWrites";
// ── THE 3D SWAP (ie-3d-conversion) ───────────────────────────
// SunkenFold3D is purely presentational: the chambers keep owning
// the gauntlet, the Matriarch, the cubs, familiarity — all of it.
// Flip the flag to false and the shipped 2D chambers return.
import SunkenFold3D from "./SunkenFold3D";
const FOLD_3D = true; // rollback switch

export const DUNGEON = {
  id: "sunken_fold",
  name: "The Sunken Fold",
  zoneLevel: 13, // Atlas: difficulty 12–15 (dial within the band)
};

const TILE = 48;
const BOSS = getBoss("phantom_matriarch");

// ------------------------------------------------------------
// Chamber geometry
// ------------------------------------------------------------
const DESCENT = {
  bounds: { minX: 1, maxX: 14, minY: 1, maxY: 6 },
  start: { x: 2, y: 3 },
  door: { x: 13, y: 3 },
};
const CRYPT = {
  bounds: { minX: 1, maxX: 13, minY: 1, maxY: 9 },
  start: { x: 2, y: 5 },
  bossSpawn: { x: 10, y: 5 },
  // Four pillars at the quarters (spec arena slot)
  pillars: [
    { x: 4, y: 3 },
    { x: 9, y: 3 },
    { x: 4, y: 7 },
    { x: 9, y: 7 },
  ],
};
const INTERACT_RANGE = 1.7;

// Prowler gauntlet — Spectral mooks that phase (family law).
const PROWLER_SPAWNS = [
  { x: 6, y: 2 },
  { x: 8, y: 5 },
  { x: 11, y: 3 },
];
const PROWLER_PHASE_EVERY_MS = 4000;
const PROWLER_PHASE_MS = 1200;
const PROWLER_KIT = { aggro: 6, range: 1.5, moveMs: 240, windMs: 600, recoverMs: 500 };

// Boss feel dials (wind-ups/thresholds on the def are spec)
const BOSS_MOVE_MS = 600;
const BOSS_ATTACK_EVERY_MS = 3200;
const FAMILIARITY_MULT = 0.35; // "first time: non-lethal" law
const CUB_STEP_MS = 650;
const CUB_SPAWNS = [
  { x: 1, y: 1 },
  { x: 13, y: 9 },
];

let floatSeq = 0;
const dist = (a, b) => Math.hypot(a.x - b.x, a.y - b.y);
const stepToward = (from, to) => ({
  x: from.x + Math.sign(to.x - from.x),
  y: from.y + Math.sign(to.y - from.y),
});

// Does a pillar break line-of-sight between two points? (the
// Spectral Roar counter — Phase 1 only, before the rubble)
const pillarBlocks = (from, to) =>
  CRYPT.pillars.some((p) => {
    const dx = to.x - from.x;
    const dy = to.y - from.y;
    const len2 = dx * dx + dy * dy;
    if (!len2) return false;
    const t = Math.max(0, Math.min(1, ((p.x - from.x) * dx + (p.y - from.y) * dy) / len2));
    const cx = from.x + t * dx;
    const cy = from.y + t * dy;
    return Math.hypot(p.x - cx, p.y - cy) < 0.9 && t > 0.05 && t < 0.95;
  });

// ============================================================
//  THE DUNGEON — stage switchboard. Each chamber mounts its own
//  controller (remount-per-stage keeps the overseer untouched);
//  Anima carries forward through the exposed setAnima verb.
// ============================================================
export default function SunkenFold({ uid, character, onBack, onDown }) {
  const [stage, setStage] = useState("descent"); // descent | crypt
  const [carryAnima, setCarryAnima] = useState(null);

  if (stage === "descent") {
    return (
      <Descent
        uid={uid}
        character={character}
        onBack={onBack}
        onDown={onDown}
        onDoor={(animaLeft) => {
          setCarryAnima(animaLeft);
          setStage("crypt");
        }}
      />
    );
  }
  return (
    <Crypt
      uid={uid}
      character={character}
      carryAnima={carryAnima}
      onBack={onBack}
      onDown={onDown}
    />
  );
}

// ============================================================
//  CHAMBER 1 — THE DESCENT (the spectral gauntlet)
// ============================================================
function Descent({ uid, character, onBack, onDown, onDoor }) {
  const [enemies, setEnemies] = useState(() =>
    PROWLER_SPAWNS.map((pos) => {
      const e = buildEnemy(ENEMY_DEFS.phantom_prowler, DUNGEON.zoneLevel, pos);
      e.phaseAt = Date.now() + PROWLER_PHASE_EVERY_MS + Math.random() * 1500;
      return e;
    })
  );
  const enemiesRef = useRef(enemies);
  useEffect(() => {
    enemiesRef.current = enemies;
  }, [enemies]);

  const [floats, setFloats] = useState([]);
  const [feltEvent, setFeltEvent] = useState(null);
  const [downed, setDowned] = useState(false);
  const downedRef = useRef(false);

  const addFloat = useCallback((pos, text, color) => {
    const id = ++floatSeq;
    setFloats((f) => [...f, { id, x: pos.x, y: pos.y, text, color }]);
    setTimeout(() => setFloats((f) => f.filter((x) => x.id !== id)), 950);
  }, []);
  const flash = useCallback((text) => {
    setFeltEvent(text);
    setTimeout(() => setFeltEvent(null), 1100);
  }, []);

  const awardKill = useCallback(
    (enemy) => {
      const xp = xpForKill(enemy.role, enemy.zoneLevel);
      addFloat(enemy.pos, `+${xp} xp`, "#8fd18a");
      if (uid) deltaXp(uid, xp).catch(() => {});
    },
    [uid, addFloat]
  );

  // ---- ability FX (one registry, AbilityFX.js) ---------------
  const { fx, spawnFx } = useAbilityFX();
  const pal = getBeingPalette(character?.beingId);

  const ctrl = usePlayerController({
    character,
    startPos: DESCENT.start,
    bounds: DESCENT.bounds,
    isBlocked: (p) =>
      enemiesRef.current.some((e) => e.alive && e.pos.x === p.x && e.pos.y === p.y),
    getTargets: () => enemiesRef.current,
    onHit: ({ targetId, damage, crit, bleedApplied }) => {
      const struck = enemiesRef.current.find((e) => e.id === targetId);
      if (struck && struck.state !== "phased")
        spawnFx("hit_spark", { pos: struck.pos, crit, tint: pal });
      setEnemies((list) =>
        list.map((e) => {
          if (e.id !== targetId || !e.alive) return e;
          // Spectral family law — untouchable while phased.
          if (e.state === "phased") {
            addFloat(e.pos, "phased", "#9fb4c9");
            return e;
          }
          let next = { ...e, anima: Math.max(0, e.anima - damage) };
          if (bleedApplied) next = applyBleedStack(next);
          addFloat(e.pos, crit ? `${damage}!` : `${damage}`, crit ? "#ffd24a" : "#e86a5c");
          if (next.anima <= 0) {
            next.alive = false;
            next.telegraphing = false;
            awardKill(next);
          }
          return next;
        })
      );
    },
    onEvent: (evt) => {
      if (evt.type === "swing" && evt.lytId) {
        spawnFx(evt.lytId, { pos: evt.origin, facing: evt.facing, step: evt.step, tint: pal });
      }
      if (evt.type === "dodge") {
        spawnFx("dodge_streak", { pos: evt.from, to: evt.to, tint: pal });
      }
      if (evt.type === "dry") {
        spawnFx("lyt_dry", { pos: evt.origin });
        flash("Lyt spent — breathe");
      }
      if (evt.type === "hurt" && evt.dodged) flash("phased!");
    },
  });

  const ctrlPosRef = useRef(ctrl.pos);
  useEffect(() => {
    ctrlPosRef.current = ctrl.pos;
  }, [ctrl.pos]);
  const animaRef = useRef(ctrl.anima);
  useEffect(() => {
    animaRef.current = ctrl.anima;
  }, [ctrl.anima]);

  // death law — no chip: the fold resets when you fall
  useEffect(() => {
    if (downedRef.current || ctrl.anima > 0) return;
    downedRef.current = true;
    setDowned(true);
    setTimeout(() => onDown?.(), 1900);
  }, [ctrl.anima, onDown]);

  const enemyStrikes = useCallback(
    (enemy) => {
      if (downedRef.current) return;
      const res = ctrl.takeHit({ attack: enemy.attack });
      addFloat(
        ctrlPosRef.current,
        res.dodged ? "phase" : `-${res.damage}`,
        res.dodged ? "#8fd18a" : "#e86a5c"
      );
    },
    [ctrl, addFloat]
  );

  const resolveSwing = useCallback(
    (enemyId) => {
      // the claw crosses the veil whether it lands or not
      const striker = enemiesRef.current.find((e) => e.id === enemyId);
      if (striker?.alive)
        spawnFx("phantom_swipe", { pos: striker.pos, toward: ctrlPosRef.current });
      setEnemies((list) =>
        list.map((e) => {
          if (e.id !== enemyId) return e;
          if (!e.alive) return { ...e, telegraphing: false };
          const after = {
            ...e,
            telegraphing: false,
            state: "aggro",
            stateUntil: Date.now() + PROWLER_KIT.recoverMs,
          };
          if (dist(e.pos, ctrlPosRef.current) <= PROWLER_KIT.range + 0.3)
            enemyStrikes(e);
          return after;
        })
      );
    },
    [enemyStrikes, spawnFx]
  );

  // enemy bleed ticks — the ONE ledger
  useEffect(() => {
    const t = setInterval(() => {
      setEnemies((list) =>
        list.map((e) => {
          if (!e.alive || !e.bleedStacks) return e;
          const { enemy, damage } = tickBleed(e);
          if (damage > 0) addFloat(e.pos, `${damage}`, "#c95c5c");
          if (enemy.anima <= 0) {
            const dead = { ...enemy, alive: false, telegraphing: false };
            awardKill(dead);
            return dead;
          }
          return enemy;
        })
      );
    }, 1000);
    return () => clearInterval(t);
  }, [addFloat, awardKill]);

  // the prowler AI — aggressor with the phase cycle on top
  useEffect(() => {
    const t = setInterval(() => {
      const now = Date.now();
      const player = ctrlPosRef.current;
      setEnemies((list) =>
        list.map((e) => {
          if (!e.alive) return e;

          // phase cycle (Spectral law)
          if (e.state === "phased") {
            if (now >= e.stateUntil) {
              // stepping back onto your side of the veil
              spawnFx("phase_shimmer", { pos: e.pos });
              return {
                ...e,
                state: "aggro",
                phaseAt: now + PROWLER_PHASE_EVERY_MS,
                stateUntil: 0,
              };
            }
            return e;
          }
          if (now >= (e.phaseAt || 0) && !e.telegraphing) {
            // slipping out — untouchable until it returns
            spawnFx("phase_shimmer", { pos: e.pos });
            return { ...e, state: "phased", stateUntil: now + PROWLER_PHASE_MS };
          }

          if (e.telegraphing || now < e.stateUntil) return e;
          const d = dist(e.pos, player);
          if (e.state === "idle") {
            return d <= PROWLER_KIT.aggro ? { ...e, state: "aggro" } : e;
          }
          if (d <= PROWLER_KIT.range) {
            setTimeout(() => resolveSwing(e.id), PROWLER_KIT.windMs);
            return {
              ...e,
              state: "telegraph",
              telegraphing: true,
              stateUntil: now + PROWLER_KIT.windMs,
            };
          }
          return {
            ...e,
            pos: stepToward(e.pos, player),
            stateUntil: now + PROWLER_KIT.moveMs,
          };
        })
      );
    }, 170);
    return () => clearInterval(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const cleared = enemies.every((e) => !e.alive);
  const nearDoor = dist(ctrl.pos, DESCENT.door) <= INTERACT_RANGE;

  const interact = useCallback(() => {
    if (cleared && dist(ctrlPosRef.current, DESCENT.door) <= INTERACT_RANGE) {
      onDoor?.(animaRef.current);
    }
  }, [cleared, onDoor]);

  useEffect(() => {
    const down = (e) => {
      if (e.key === "e" || e.key === "E") {
        e.preventDefault();
        interact();
      }
    };
    window.addEventListener("keydown", down);
    return () => window.removeEventListener("keydown", down);
  }, [interact]);

  const anim = usePlayerAnimation(ctrl.pos);
  const W = (DESCENT.bounds.maxX + 2) * TILE;
  const H = (DESCENT.bounds.maxY + 2) * TILE;

  if (FOLD_3D) {
    return (
      <>
        <SunkenFold3D
          stage="descent"
          character={character}
          ctrlPos={ctrl.pos}
          ctrlFacing={ctrl.facing}
          dim={ctrl.dodging || ctrl.isInvulnerable()}
          swing={ctrl.lastSwing}
          dodging={ctrl.dodging}
          keysRef={ctrl.keysRef}
          onAttack={ctrl.attack}
          onDodge={ctrl.dodge}
          enemies={enemies}
          floats={floats}
          geo={{ bounds: DESCENT.bounds, door: DESCENT.door, doorOpen: cleared }}
          prompt={cleared && nearDoor ? "E — enter the crypt" : null}
          feltEvent={feltEvent}
          downed={downed}
          onBack={onBack}
        />
        {/* HUD — the fold's own bars, floated over the 3D view */}
        <div style={{ position: "fixed", top: 8, left: "50%",
          transform: "translateX(-50%)", zIndex: 30,
          width: "min(560px, calc(100vw - 24px))" }}>
          <Hud title={`${DUNGEON.name} · The Descent`} ctrl={ctrl} character={character} width={560} />
        </div>
        {/* mobile interact — the 3D chrome carries attack/dodge only */}
        {cleared && nearDoor ? (
          <button style={{ ...S.interactPad, zIndex: 30 }} onClick={interact}>◆</button>
        ) : null}
      </>
    );
  }

  return (
    <div style={S.void}>
      <Hud
        title={`${DUNGEON.name} · The Descent`}
        ctrl={ctrl}
        character={character}
        width={W}
      />
      <div style={{ ...S.chamber, width: W, height: H }}>
        <div style={S.cryptGround} />
        {/* the crypt door — sealed until the gauntlet falls */}
        <div
          style={{
            ...S.cryptDoor,
            left: (DESCENT.door.x - 0.5) * TILE,
            top: (DESCENT.door.y - 1.4) * TILE,
            ...(cleared ? S.cryptDoorOpen : null),
          }}
        >
          <div style={S.cryptDoorLabel}>{cleared ? "the crypt opens" : "sealed"}</div>
        </div>

        {enemies.map((e) =>
          e.alive ? (
            <ProwlerVisual key={e.id} enemy={e} />
          ) : (
            <div key={e.id} style={{ ...S.downMark, left: e.pos.x * TILE, top: e.pos.y * TILE }}>
              ✦
            </div>
          )
        )}

        {/* ability visuals — the one registry, world-space */}
        <AbilityFXLayer fx={fx} tile={TILE} />

        {floats.map((f) => (
          <div
            key={f.id}
            style={{
              ...S.float,
              left: f.x * TILE + TILE / 2,
              top: f.y * TILE - TILE * 0.4,
              color: f.color,
            }}
          >
            {f.text}
          </div>
        ))}

        <AnnihilationPlayer
          character={character}
          pos={ctrl.pos}
          facing={ctrl.facing}
          walkFrame={anim.walkFrame}
          isIdle={anim.isIdle}
          idleFrame={anim.idleFrame}
          tile={TILE}
          opacity={ctrl.dodging || ctrl.isInvulnerable() ? 0.45 : 1}
          swing={ctrl.lastSwing}
        />
      </div>

      {cleared && nearDoor ? <div style={S.prompt}>E — enter the crypt</div> : null}
      {feltEvent ? <div style={S.felt}>{feltEvent}</div> : null}
      {downed ? <DownVeil /> : null}

      <div style={S.controls}>
        the prowlers phase — strike them while they're solid
      </div>
      <button style={S.back} onClick={onBack}>
        ← climb back to the Grove
      </button>

      <MobileDpad keysRef={ctrl.keysRef} />
      <MobileActionPad onAttack={ctrl.attack} onDodge={ctrl.dodge} />
      {cleared && nearDoor ? (
        <button style={S.interactPad} onClick={interact}>
          ◆
        </button>
      ) : null}
    </div>
  );
}

// ============================================================
//  CHAMBER 2 — THE CRYPT (the Phantom Matriarch)
//  The nine-slot template, live. She is a Choreographer: a
//  scripted rotation with telegraphs, the cub hook, the phase
//  break at 40%, and the enrage clock.
// ============================================================
function Crypt({ uid, character, carryAnima, onBack, onDown }) {
  const [boss, setBoss] = useState(() => {
    const b = buildEnemy(
      {
        id: BOSS.id,
        name: BOSS.name,
        family: BOSS.family,
        role: BOSS.role,
        ai: BOSS.ai,
        anima: BOSS.anima,
        lyf: BOSS.lyf,
      },
      DUNGEON.zoneLevel,
      CRYPT.bossSpawn
    );
    b.phase = 1;
    return b;
  });
  const bossRef = useRef(boss);
  useEffect(() => {
    bossRef.current = boss;
  }, [boss]);

  const [cubs, setCubs] = useState([]);
  const cubsRef = useRef(cubs);
  useEffect(() => {
    cubsRef.current = cubs;
  }, [cubs]);

  const [fightOn, setFightOn] = useState(false);
  const [telegraph, setTelegraph] = useState(null); // { attackId, target, litTile }
  const [floats, setFloats] = useState([]);
  const [feltEvent, setFeltEvent] = useState(null);
  const [downed, setDowned] = useState(false);
  const [victory, setVictory] = useState(false); // motes + monuments
  const [whisper, setWhisper] = useState(null);
  const downedRef = useRef(false);
  const victoryRef = useRef(false);
  const fightStartRef = useRef(0);
  const seenAttacks = useRef(new Set()); // familiarity law
  const enrageMultRef = useRef(1);
  const awardedRef = useRef(false);

  const addFloat = useCallback((pos, text, color) => {
    const id = ++floatSeq;
    setFloats((f) => [...f, { id, x: pos.x, y: pos.y, text, color }]);
    setTimeout(() => setFloats((f) => f.filter((x) => x.id !== id)), 950);
  }, []);
  const flash = useCallback((text) => {
    setFeltEvent(text);
    setTimeout(() => setFeltEvent(null), 1400);
  }, []);

  // ---- ability FX (one registry, AbilityFX.js) ---------------
  const { fx, spawnFx } = useAbilityFX();
  const pal = getBeingPalette(character?.beingId);

  // targets: her + the cubs (kill or peel — the hook)
  const getTargets = useCallback(
    () => [bossRef.current, ...cubsRef.current],
    []
  );

  const ctrl = usePlayerController({
    character,
    startPos: CRYPT.start,
    bounds: CRYPT.bounds,
    isBlocked: (p) => {
      const b = bossRef.current;
      if (b.alive && b.phase === 1) {
        // pillars stand in Phase 1; rubble is walkable in Phase 2
        if (CRYPT.pillars.some((pl) => pl.x === p.x && pl.y === p.y)) return true;
      }
      if (b.alive && dist(p, b.pos) < 1.1) return true;
      return false;
    },
    getTargets,
    onHit: ({ targetId, damage, crit, bleedApplied }) => {
      const b = bossRef.current;
      const struckPos =
        targetId === b.id
          ? b.pos
          : cubsRef.current.find((c) => c.id === targetId)?.pos;
      if (struckPos) spawnFx("hit_spark", { pos: struckPos, crit, tint: pal });
      if (targetId === b.id && b.alive) {
        setBoss((cur) => {
          if (!cur.alive) return cur;
          let next = { ...cur, anima: Math.max(0, cur.anima - damage) };
          if (bleedApplied) next = applyBleedStack(next);
          addFloat(cur.pos, crit ? `${damage}!` : `${damage}`, crit ? "#ffd24a" : "#e86a5c");
          // Phase transition — Anima-threshold triggered (spec law)
          if (next.phase === 1 && next.anima <= BOSS.phase2At) {
            next.phase = 2;
            flash("the pillars shatter — she has nothing left to hide behind");
          }
          if (next.anima <= 0 && next.alive) {
            next.alive = false;
            fall();
          }
          return next;
        });
        return;
      }
      // a cub
      setCubs((list) =>
        list.map((c) => {
          if (c.id !== targetId || !c.alive) return c;
          const next = { ...c, anima: Math.max(0, c.anima - damage) };
          addFloat(c.pos, `${damage}`, "#e86a5c");
          if (next.anima <= 0) next.alive = false;
          return next;
        }).filter((c) => c.alive)
      );
    },
    onEvent: (evt) => {
      if (evt.type === "swing" && evt.lytId) {
        spawnFx(evt.lytId, { pos: evt.origin, facing: evt.facing, step: evt.step, tint: pal });
      }
      if (evt.type === "dodge") {
        spawnFx("dodge_streak", { pos: evt.from, to: evt.to, tint: pal });
      }
      if (evt.type === "dry") {
        spawnFx("lyt_dry", { pos: evt.origin });
        flash("Lyt spent — breathe");
      }
      if (evt.type === "hurt" && evt.dodged) flash("phased!");
    },
  });

  const ctrlPosRef = useRef(ctrl.pos);
  useEffect(() => {
    ctrlPosRef.current = ctrl.pos;
  }, [ctrl.pos]);

  // Anima carries in from the Descent (the crypt is not a rest)
  useEffect(() => {
    if (typeof carryAnima === "number") {
      ctrl.setAnima((a) => Math.min(a, Math.max(1, carryAnima)));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // fight opens when you step into her half
  useEffect(() => {
    if (fightOn || victoryRef.current) return;
    if (dist(ctrl.pos, bossRef.current.pos) <= 6) {
      setFightOn(true);
      fightStartRef.current = Date.now();
      flash(`"${BOSS.flavorLine}"`);
    }
  }, [ctrl.pos, fightOn, flash]);

  // death law — no chip: she resets to full when you fall
  useEffect(() => {
    if (downedRef.current || ctrl.anima > 0) return;
    downedRef.current = true;
    setDowned(true);
    setTimeout(() => onDown?.(), 2100);
  }, [ctrl.anima, onDown]);

  // boss bleed ticks — the ONE ledger
  useEffect(() => {
    const t = setInterval(() => {
      setBoss((b) => {
        if (!b.alive || !b.bleedStacks) return b;
        const { enemy, damage } = tickBleed(b);
        if (damage > 0) addFloat(b.pos, `${damage}`, "#c95c5c");
        let next = enemy;
        if (next.phase === 1 && next.anima <= BOSS.phase2At) {
          next = { ...next, phase: 2 };
          flash("the pillars shatter — she has nothing left to hide behind");
        }
        if (next.anima <= 0 && next.alive !== false) {
          fall();
          return { ...next, alive: false };
        }
        return next;
      });
    }, 1000);
    return () => clearInterval(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ---- a boss hit landing — familiarity + enrage applied ------
  const bossStrikes = useCallback(
    (attackDef) => {
      if (downedRef.current) return;
      const firstTime = !seenAttacks.current.has(attackDef.id);
      seenAttacks.current.add(attackDef.id);
      const raw = Math.round(
        attackDef.damage *
          enrageMultRef.current *
          (firstTime ? FAMILIARITY_MULT : 1)
      );
      const res = ctrl.takeHit({ attack: raw, skillBonus: 0 });
      addFloat(
        ctrlPosRef.current,
        res.dodged ? "phase" : `-${res.damage}`,
        res.dodged ? "#8fd18a" : "#e86a5c"
      );
      if (firstTime && !res.dodged) {
        flash(`${attackDef.name} — she showed you once. counter: ${attackDef.counter}`);
      }
    },
    [ctrl, addFloat, flash]
  );

  // ---- THE ROTATION — the Choreographer's clock ----------------
  useEffect(() => {
    if (!fightOn) return;
    let cancelled = false;
    let nextAttackAt = Date.now() + 1800;
    let rotation = 0;

    const t = setInterval(() => {
      if (cancelled) return;
      const now = Date.now();
      const b = bossRef.current;
      if (!b.alive || downedRef.current) return;

      // enrage clock (the only time-based mechanic — spec law)
      const elapsed = now - fightStartRef.current;
      if (elapsed >= BOSS.enrageMs) {
        const steps = 1 + Math.floor((elapsed - BOSS.enrageMs) / BOSS.enrageStepMs);
        const mult = Math.pow(BOSS.enrageAttackMult, steps);
        if (mult !== enrageMultRef.current) {
          enrageMultRef.current = mult;
          flash("SHE REMEMBERS — her fury multiplies");
        }
      }

      if (b.telegraphing) return;

      if (now >= nextAttackAt) {
        // pick the next attack legal in this phase
        const pool = Object.values(BOSS.attacks).filter((a) =>
          a.phase.includes(b.phase)
        );
        const attackDef = pool[rotation % pool.length];
        rotation += 1;
        nextAttackAt = now + BOSS_ATTACK_EVERY_MS;
        beginAttack(attackDef);
        return;
      }

      // idle pursuit between attacks
      if (now >= b.stateUntil) {
        const toward = stepToward(b.pos, ctrlPosRef.current);
        const onPillar =
          b.phase === 1 &&
          CRYPT.pillars.some((p) => p.x === toward.x && p.y === toward.y);
        setBoss((cur) =>
          cur.alive
            ? { ...cur, pos: onPillar ? cur.pos : toward, stateUntil: now + BOSS_MOVE_MS }
            : cur
        );
      }
    }, 150);
    return () => {
      cancelled = true;
      clearInterval(t);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fightOn]);

  // begin a telegraphed attack; resolve it after the wind-up
  const beginAttack = useCallback(
    (attackDef) => {
      const b = bossRef.current;
      const target = { ...ctrlPosRef.current }; // the tell locks your position at wind-up
      let litTile = null;
      if (attackDef.id === "haunting_presence") {
        // one lit tile, reachable in the wind-up — the counter
        litTile = {
          x: Math.max(
            CRYPT.bounds.minX,
            Math.min(CRYPT.bounds.maxX, target.x + (Math.random() < 0.5 ? -2 : 2))
          ),
          y: Math.max(
            CRYPT.bounds.minY,
            Math.min(CRYPT.bounds.maxY, target.y + (Math.random() < 0.5 ? -1 : 1))
          ),
        };
      }
      setBoss((cur) => ({ ...cur, telegraphing: true }));
      setTelegraph({ attackId: attackDef.id, target, litTile, from: { ...b.pos } });

      setTimeout(() => {
        setBoss((cur) => ({ ...cur, telegraphing: false }));
        setTelegraph(null);
        const bossNow = bossRef.current;
        if (!bossNow.alive || downedRef.current) return;
        const p = ctrlPosRef.current;

        if (attackDef.id === "phantom_lunge") {
          // she dashes to where you WERE — sideways is safety.
          // The dash made visible: streak from her to the mark.
          spawnFx("phantom_lunge", { pos: { ...bossNow.pos }, to: { ...target } });
          setBoss((cur) => (cur.alive ? { ...cur, pos: { ...target } } : cur));
          if (dist(p, target) <= 1.3) bossStrikes(attackDef);
          return;
        }
        if (attackDef.id === "claw_sweep") {
          // the full wheel of phantom claws, 2.2-tile radius
          spawnFx("claw_sweep", { pos: { ...bossNow.pos } });
          if (dist(p, bossNow.pos) <= 2.2) bossStrikes(attackDef);
          return;
        }
        if (attackDef.id === "spectral_roar") {
          // a cone toward your wind-up position; pillars block it
          spawnFx("spectral_roar", { pos: { ...bossNow.pos }, toward: { ...target } });
          const inCone = (() => {
            const toP = { x: p.x - bossNow.pos.x, y: p.y - bossNow.pos.y };
            const toT = { x: target.x - bossNow.pos.x, y: target.y - bossNow.pos.y };
            const dP = Math.hypot(toP.x, toP.y);
            const dT = Math.hypot(toT.x, toT.y) || 1;
            if (dP > 7 || dP === 0) return false;
            const cos = (toP.x * toT.x + toP.y * toT.y) / (dP * dT);
            return cos > 0.75; // ~40° cone
          })();
          if (inCone && !pillarBlocks(bossNow.pos, p)) bossStrikes(attackDef);
          else if (inCone) flash("the pillar takes the roar");
          return;
        }
        if (attackDef.id === "haunting_presence") {
          // arena-wide unless you stand in the light — the crypt
          // dims, and the one lit tile holds
          spawnFx("haunting_presence", { pos: { ...bossNow.pos } });
          if (litTile) spawnFx("safe_light", { pos: { ...litTile } });
          if (!litTile || dist(p, litTile) > 0.9) bossStrikes(attackDef);
          else flash("the light holds");
          return;
        }
      }, attackDef.windupMs);
    },
    [bossStrikes, flash, spawnFx]
  );

  // ---- PHASE II, SEEN — the moment the threshold trips, every
  // pillar bursts into flying stone. One watcher, either trigger
  // path (player hit or bleed tick) — the state is the truth.
  const shatteredRef = useRef(false);
  useEffect(() => {
    if (boss.phase === 2 && !shatteredRef.current) {
      shatteredRef.current = true;
      CRYPT.pillars.forEach((p) => spawnFx("pillar_shatter", { pos: p }));
    }
  }, [boss.phase, spawnFx]);

  // ---- THE HOOK — cubs every 30s; each one home heals her 10% --
  useEffect(() => {
    if (!fightOn) return;
    const t = setInterval(() => {
      if (!bossRef.current.alive || downedRef.current) return;
      setCubs((list) => [
        ...list,
        ...CUB_SPAWNS.map((pos) =>
          buildEnemy(ENEMY_DEFS.phantom_cub, DUNGEON.zoneLevel, pos)
        ),
      ]);
      flash("two Phantom Cubs — kill them or peel them, they must not reach her");
    }, BOSS.cubEveryMs);
    return () => clearInterval(t);
  }, [fightOn, flash]);

  // cubs walk home — home is HER
  useEffect(() => {
    const t = setInterval(() => {
      const b = bossRef.current;
      if (!b.alive) return;
      setCubs((list) =>
        list
          .map((c) => {
            const next = { ...c, pos: stepToward(c.pos, b.pos) };
            if (dist(next.pos, b.pos) < 1.1) {
              const heal = Math.round(BOSS.anima * BOSS.cubHealPct);
              setBoss((cur) =>
                cur.alive
                  ? { ...cur, anima: Math.min(BOSS.anima, cur.anima + heal) }
                  : cur
              );
              spawnFx("soul_absorb", { pos: b.pos });
              addFloat(b.pos, `+${Math.round(BOSS.anima * BOSS.cubHealPct)}`, "#8fd18a");
              return null; // absorbed
            }
            return next;
          })
          .filter(Boolean)
      );
    }, CUB_STEP_MS);
    return () => clearInterval(t);
  }, [addFloat, spawnFx]);

  // ---- THE FALL — death animation, monuments, the drop table ---
  const fall = useCallback(() => {
    if (victoryRef.current) return;
    victoryRef.current = true;
    setVictory(true);
    setCubs([]);
    if (!awardedRef.current && uid) {
      awardedRef.current = true;
      const xp = xpForKill("sub_boss", DUNGEON.zoneLevel);
      deltaXp(uid, xp).catch(() => {});
      recordDungeonClear(uid, DUNGEON.id)
        .then(({ firstClear }) => {
          const bm =
            BOSS.drops.clearBloodMoney +
            (firstClear ? BOSS.drops.firstClearBloodMoney : 0);
          deltaBloodMoney(uid, bm).catch(() => {});
          flash(
            firstClear
              ? `first clear — +${xp} xp · +${bm} 🩸 · a Tome hums in the rubble (Willow's Forge will wake it)`
              : `+${xp} xp · +${bm} 🩸`
          );
        })
        .catch(() => {});
    }
  }, [uid, flash]);

  // monuments whisper when you come close (names are hers to keep)
  useEffect(() => {
    if (!victory) return;
    const near = CRYPT.pillars.find((p) => dist(ctrl.pos, p) <= 1.2);
    setWhisper(near ? "…a name, soft, and gone…" : null);
  }, [victory, ctrl.pos]);

  const anim = usePlayerAnimation(ctrl.pos);
  const W = (CRYPT.bounds.maxX + 2) * TILE;
  const H = (CRYPT.bounds.maxY + 2) * TILE;
  const attackDef = telegraph ? BOSS.attacks[telegraph.attackId] : null;

  if (FOLD_3D) {
    return (
      <>
        <SunkenFold3D
          stage="crypt"
          character={character}
          ctrlPos={ctrl.pos}
          ctrlFacing={ctrl.facing}
          dim={ctrl.dodging || ctrl.isInvulnerable()}
          swing={ctrl.lastSwing}
          dodging={ctrl.dodging}
          keysRef={ctrl.keysRef}
          onAttack={ctrl.attack}
          onDodge={ctrl.dodge}
          enemies={[]}
          boss={boss.alive ? boss : null}
          cubs={cubs}
          floats={floats}
          geo={{ bounds: CRYPT.bounds, pillars: CRYPT.pillars }}
          phase2={!(boss.phase === 1 && boss.alive)}
          feltEvent={feltEvent}
          downed={downed}
          onBack={onBack}
        />
        {/* HUD — Anima/Lyt + her bar, floated over the 3D view */}
        <div style={{ position: "fixed", top: 8, left: "50%",
          transform: "translateX(-50%)", zIndex: 30,
          width: "min(560px, calc(100vw - 24px))" }}>
          <Hud title={`${DUNGEON.name} · The Crypt`} ctrl={ctrl} character={character} width={560} boss={boss.alive ? boss : null} />
        </div>
        {whisper ? <div style={{ ...S.whisper, zIndex: 30 }}>{whisper}</div> : null}
        {victory ? (
          <div style={{ ...S.victoryLine, zIndex: 30 }}>
            {BOSS.deathText} <span style={S.victorySub}>— you defeated her, but she remembered you.</span>
          </div>
        ) : null}
      </>
    );
  }

  return (
    <div style={S.void}>
      <Hud
        title={`${DUNGEON.name} · The Crypt`}
        ctrl={ctrl}
        character={character}
        width={W}
        boss={boss.alive ? boss : null}
      />
      <div style={{ ...S.chamber, width: W, height: H }}>
        <div style={S.cryptGround} />
        <div style={S.cryptRing} />

        {/* pillars — cover in Phase 1, rubble in Phase 2 */}
        {CRYPT.pillars.map((p, i) => (
          <div
            key={i}
            style={{
              ...(boss.phase === 1 && boss.alive ? S.pillar : S.rubble),
              left: p.x * TILE,
              top: (p.y - (boss.phase === 1 && boss.alive ? 1 : 0.2)) * TILE,
              ...(victory ? S.monument : null),
            }}
          >
            {victory ? "🕯" : null}
          </div>
        ))}

        {/* telegraphs */}
        {telegraph && attackDef ? (
          <>
            {telegraph.attackId === "phantom_lunge" ? (
              <div
                style={{
                  ...S.tellTile,
                  left: telegraph.target.x * TILE - 6,
                  top: telegraph.target.y * TILE - 6,
                }}
              />
            ) : null}
            {telegraph.attackId === "claw_sweep" ? (
              <div
                style={{
                  ...S.tellCircle,
                  left: (boss.pos.x - 2.2) * TILE,
                  top: (boss.pos.y - 2.2) * TILE,
                  width: 4.4 * TILE,
                  height: 4.4 * TILE,
                }}
              />
            ) : null}
            {telegraph.attackId === "spectral_roar" ? (
              <div
                style={{
                  ...S.tellCone,
                  left: boss.pos.x * TILE,
                  top: boss.pos.y * TILE,
                  transform: `rotate(${Math.atan2(
                    telegraph.target.y - telegraph.from.y,
                    telegraph.target.x - telegraph.from.x
                  )}rad)`,
                }}
              />
            ) : null}
            {telegraph.attackId === "haunting_presence" ? (
              <>
                <div style={S.tellArena} />
                {telegraph.litTile ? (
                  <div
                    style={{
                      ...S.litTile,
                      left: telegraph.litTile.x * TILE - 4,
                      top: telegraph.litTile.y * TILE - 4,
                    }}
                  />
                ) : null}
              </>
            ) : null}
            <div style={S.tellName}>{attackDef.name}</div>
          </>
        ) : null}

        {/* the cubs */}
        {cubs.map((c) => (
          <div key={c.id} style={{ ...S.cub, left: c.pos.x * TILE, top: c.pos.y * TILE }}>
            <div style={S.cubBody} />
            <div style={S.cubHp}>
              <div
                style={{ ...S.cubHpFill, width: `${(c.anima / c.animaMax) * 100}%` }}
              />
            </div>
          </div>
        ))}

        {/* HER */}
        {boss.alive ? (
          <div
            style={{
              ...S.matriarch,
              left: (boss.pos.x - 0.6) * TILE,
              top: (boss.pos.y - 1.1) * TILE,
              ...(boss.telegraphing ? S.matriarchTell : null),
              ...(boss.phase === 2 ? S.matriarchP2 : null),
            }}
          >
            <div style={S.matriarchBody} />
            <div style={S.matriarchEyes} />
            {boss.bleedStacks ? (
              <div style={S.bleedTag}>
                🩸×{boss.bleedStacks}
                {boss.bleedStacks >= MAX_BLEED_STACKS ? " MAX" : ""}
              </div>
            ) : null}
          </div>
        ) : (
          victory && (
            <div
              style={{ ...S.motes, left: boss.pos.x * TILE, top: boss.pos.y * TILE }}
            >
              ✦ ✧ ✦
            </div>
          )
        )}

        {/* ability visuals — the one registry, world-space */}
        <AbilityFXLayer fx={fx} tile={TILE} />

        {floats.map((f) => (
          <div
            key={f.id}
            style={{
              ...S.float,
              left: f.x * TILE + TILE / 2,
              top: f.y * TILE - TILE * 0.4,
              color: f.color,
            }}
          >
            {f.text}
          </div>
        ))}

        <AnnihilationPlayer
          character={character}
          pos={ctrl.pos}
          facing={ctrl.facing}
          walkFrame={anim.walkFrame}
          isIdle={anim.isIdle}
          idleFrame={anim.idleFrame}
          tile={TILE}
          opacity={ctrl.dodging || ctrl.isInvulnerable() ? 0.45 : 1}
          swing={ctrl.lastSwing}
        />
      </div>

      {whisper ? <div style={S.whisper}>{whisper}</div> : null}
      {feltEvent ? <div style={S.felt}>{feltEvent}</div> : null}
      {downed ? <DownVeil noChip /> : null}
      {victory ? (
        <div style={S.victoryLine}>
          {BOSS.deathText} <span style={S.victorySub}>— you defeated her, but she remembered you.</span>
        </div>
      ) : null}

      <div style={S.controls}>
        every attack is telegraphed · every attack has a counter · the cubs must not reach her
      </div>
      <button style={S.back} onClick={victory ? onBack : onBack}>
        ← {victory ? "walk back into the light" : "abandon the crypt"}
      </button>

      <MobileDpad keysRef={ctrl.keysRef} />
      <MobileActionPad onAttack={ctrl.attack} onDodge={ctrl.dodge} />
    </div>
  );
}

// ------------------------------------------------------------
// Shared chrome
// ------------------------------------------------------------
function Hud({ title, ctrl, character, width, boss }) {
  return (
    <div style={{ ...S.hud, maxWidth: width }}>
      <div style={S.hudTitle}>{title}</div>
      {boss ? (
        <div style={S.bossBarRow}>
          <span style={S.bossName}>
            {BOSS.name} {boss.phase === 2 ? "· PHASE II" : ""}
          </span>
          <div style={S.bossTrack}>
            <div
              style={{
                ...S.bossFill,
                width: `${(boss.anima / BOSS.anima) * 100}%`,
              }}
            />
          </div>
          <span style={S.bossNum}>{boss.anima}</span>
        </div>
      ) : null}
      <Bar label="Anima" value={ctrl.anima} max={ctrl.animaMax} color="#e86a5c" />
      <div style={S.lytRow}>
        <span style={S.lytLabel}>Lyt</span>
        {Array.from({ length: ctrl.lytMax }).map((_, i) => (
          <span
            key={i}
            style={{
              ...S.lytPip,
              ...(i < Math.floor(ctrl.lyt) ? S.lytPipFull : null),
            }}
          />
        ))}
        <span style={S.comboText}>
          {ctrl.comboStep ? ` combo ${ctrl.comboStep}/${ctrl.comboLength}` : ""}
        </span>
      </div>
      <div style={S.barHolder}>
        <SkillBar character={character} compact />
      </div>
    </div>
  );
}

function ProwlerVisual({ enemy }) {
  const phased = enemy.state === "phased";
  return (
    <div
      style={{
        ...S.prowler,
        left: enemy.pos.x * TILE,
        top: enemy.pos.y * TILE - TILE * 0.35,
        opacity: phased ? 0.25 : 1,
        ...(enemy.telegraphing ? S.enemyTelegraph : null),
      }}
    >
      <div style={S.prowlerBody}>
        <div style={S.prowlerStripes} />
        <div style={S.eyes} />
      </div>
      <div style={S.enemyHp}>
        <div
          style={{
            ...S.enemyHpFill,
            width: `${(enemy.anima / enemy.animaMax) * 100}%`,
          }}
        />
      </div>
      {enemy.bleedStacks ? (
        <div style={S.bleedTag}>🩸×{enemy.bleedStacks}</div>
      ) : null}
    </div>
  );
}

function Bar({ label, value, max, color }) {
  return (
    <div style={S.statBar}>
      <span style={S.statBarLabel}>{label}</span>
      <div style={S.statBarTrack}>
        <div
          style={{
            ...S.statBarFill,
            width: `${Math.max(0, Math.min(100, (value / max) * 100))}%`,
            background: color,
          }}
        />
      </div>
      <span style={S.statBarNum}>
        {Math.ceil(value)}/{max}
      </span>
    </div>
  );
}

function DownVeil({ noChip }) {
  return (
    <div style={S.downVeil}>
      <div style={S.downText}>the fold takes you back</div>
      <div style={S.downSub}>
        you wake at the Old Fold — {noChip ? "she stands whole again. no chipping her down." : "nothing is lost, nothing is rested"}
      </div>
    </div>
  );
}

const gold = "#e8a33d";
const spectral = "#c9a4e8";
const S = {
  void: {
    minHeight: "100vh",
    background:
      "radial-gradient(ellipse at 50% 30%, #16121f 0%, #0a0812 55%, #050408 100%)",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    padding: "14px 10px 120px",
    boxSizing: "border-box",
    fontFamily: "Georgia, 'Times New Roman', serif",
    overflow: "hidden",
  },
  hud: { width: "100%", marginBottom: 8 },
  hudTitle: {
    color: spectral,
    fontSize: 17,
    letterSpacing: "0.1em",
    textAlign: "center",
    marginBottom: 7,
    textShadow: "0 0 18px rgba(201,164,232,0.35)",
  },
  bossBarRow: { display: "flex", alignItems: "center", gap: 8, marginBottom: 7 },
  bossName: {
    color: spectral,
    fontSize: 11,
    letterSpacing: "0.1em",
    whiteSpace: "nowrap",
  },
  bossTrack: {
    flex: 1,
    height: 12,
    background: "rgba(0,0,0,0.6)",
    border: "1px solid rgba(201,164,232,0.5)",
    borderRadius: 999,
    overflow: "hidden",
  },
  bossFill: {
    height: "100%",
    background: "linear-gradient(90deg, #7a5aa0, #c9a4e8)",
    transition: "width 0.15s",
  },
  bossNum: { color: "#b5a284", fontSize: 11, width: 44, textAlign: "right" },
  statBar: { display: "flex", alignItems: "center", gap: 8, marginBottom: 5 },
  statBarLabel: {
    color: "#8a7a5c",
    fontSize: 10,
    letterSpacing: "0.18em",
    textTransform: "uppercase",
    width: 44,
    textAlign: "right",
  },
  statBarTrack: {
    flex: 1,
    height: 10,
    background: "rgba(0,0,0,0.5)",
    border: "1px solid rgba(232,163,61,0.25)",
    borderRadius: 999,
    overflow: "hidden",
  },
  statBarFill: { height: "100%", transition: "width 0.15s" },
  statBarNum: { color: "#b5a284", fontSize: 11, width: 64 },
  lytRow: { display: "flex", alignItems: "center", gap: 5, marginBottom: 6 },
  lytLabel: {
    color: "#8a7a5c",
    fontSize: 10,
    letterSpacing: "0.18em",
    textTransform: "uppercase",
    width: 44,
    textAlign: "right",
  },
  lytPip: {
    width: 12,
    height: 12,
    borderRadius: "50%",
    border: "1px solid rgba(232,163,61,0.5)",
    background: "rgba(0,0,0,0.4)",
  },
  lytPipFull: { background: gold, boxShadow: "0 0 8px rgba(232,163,61,0.5)" },
  comboText: { color: "#8fd18a", fontSize: 11, marginLeft: 8, fontStyle: "italic" },
  barHolder: { marginTop: 2 },
  chamber: {
    position: "relative",
    border: "1px solid rgba(201,164,232,0.35)",
    borderRadius: 10,
    overflow: "hidden",
    boxShadow: "0 0 40px rgba(201,164,232,0.08), inset 0 0 80px rgba(0,0,0,0.7)",
    maxWidth: "100%",
  },
  cryptGround: {
    position: "absolute",
    inset: 0,
    background:
      "radial-gradient(ellipse at 50% 40%, rgba(120,90,160,0.08), transparent 65%)," +
      "repeating-linear-gradient(0deg, #14101c 0, #14101c 47px, #191423 47px, #191423 48px)," +
      "repeating-linear-gradient(90deg, #14101c 0, #14101c 47px, #191423 47px, #191423 48px)",
  },
  cryptRing: {
    position: "absolute",
    left: "8%",
    top: "8%",
    right: "8%",
    bottom: "8%",
    border: "1px dashed rgba(201,164,232,0.15)",
    borderRadius: "50%",
    pointerEvents: "none",
  },
  cryptDoor: {
    position: "absolute",
    width: TILE * 1.4,
    height: TILE * 1.8,
    background: "radial-gradient(ellipse at 50% 20%, #0d0a12 30%, #000 100%)",
    border: "2px solid rgba(201,164,232,0.2)",
    borderRadius: "50% 50% 6px 6px",
    zIndex: 3,
    transition: "box-shadow 0.6s, border-color 0.6s",
  },
  cryptDoorOpen: {
    borderColor: "rgba(201,164,232,0.8)",
    boxShadow: "0 0 30px rgba(201,164,232,0.4), inset 0 0 24px rgba(201,164,232,0.15)",
  },
  cryptDoorLabel: {
    position: "absolute",
    left: 0,
    right: 0,
    bottom: -16,
    textAlign: "center",
    color: spectral,
    fontSize: 9.5,
    letterSpacing: "0.2em",
    textTransform: "uppercase",
  },
  pillar: {
    position: "absolute",
    width: TILE,
    height: TILE * 2,
    background: "linear-gradient(180deg, #4a4055 0%, #2d2738 100%)",
    border: "1px solid rgba(201,164,232,0.25)",
    borderRadius: "6px 6px 2px 2px",
    boxShadow: "0 0 18px rgba(0,0,0,0.6)",
    zIndex: 6,
    textAlign: "center",
    fontSize: 20,
    lineHeight: `${TILE * 2}px`,
  },
  rubble: {
    position: "absolute",
    width: TILE,
    height: TILE * 0.5,
    background: "#2d2738",
    borderRadius: "40% 60% 50% 50%",
    opacity: 0.8,
    zIndex: 4,
    textAlign: "center",
    fontSize: 18,
  },
  monument: {
    background: "linear-gradient(180deg, #3a3348 0%, #241f30 100%)",
    border: "1px solid rgba(201,164,232,0.5)",
    boxShadow: "0 0 22px rgba(201,164,232,0.35)",
    height: TILE * 1.2,
    lineHeight: `${TILE * 1.2}px`,
    borderRadius: "50% 50% 4px 4px",
    opacity: 1,
  },
  tellTile: {
    position: "absolute",
    width: TILE + 12,
    height: TILE + 12,
    border: "2px solid #e86a5c",
    borderRadius: 8,
    background: "rgba(232,106,92,0.15)",
    zIndex: 5,
    animation: "none",
  },
  tellCircle: {
    position: "absolute",
    border: "2px solid #e86a5c",
    borderRadius: "50%",
    background: "rgba(232,106,92,0.10)",
    zIndex: 5,
    transform: "translate(24px, 24px)",
  },
  tellCone: {
    position: "absolute",
    width: 7 * TILE,
    height: 5 * TILE,
    background:
      "conic-gradient(from -0.35rad at 0% 50%, rgba(232,106,92,0.22) 0deg, rgba(232,106,92,0.22) 40deg, transparent 40deg)",
    transformOrigin: "0% 50%",
    zIndex: 5,
    pointerEvents: "none",
    marginTop: -2.5 * TILE + TILE / 2,
    marginLeft: TILE / 2,
  },
  tellArena: {
    position: "absolute",
    inset: 0,
    background: "rgba(120,90,160,0.14)",
    zIndex: 4,
    pointerEvents: "none",
  },
  litTile: {
    position: "absolute",
    width: TILE + 8,
    height: TILE + 8,
    border: "2px solid #ffd24a",
    borderRadius: 8,
    background: "rgba(255,210,74,0.22)",
    boxShadow: "0 0 24px rgba(255,210,74,0.5)",
    zIndex: 6,
  },
  tellName: {
    position: "absolute",
    top: 8,
    left: 0,
    right: 0,
    textAlign: "center",
    color: "#e86a5c",
    fontSize: 13,
    letterSpacing: "0.2em",
    textTransform: "uppercase",
    zIndex: 20,
    textShadow: "0 0 12px rgba(232,106,92,0.6)",
  },
  matriarch: {
    position: "absolute",
    width: TILE * 2.2,
    height: TILE * 2.2,
    zIndex: 9,
    transition: "left 0.24s linear, top 0.24s linear, filter 0.2s",
  },
  matriarchTell: { filter: "drop-shadow(0 0 16px #e86a5c) brightness(1.4)" },
  matriarchP2: { filter: "drop-shadow(0 0 20px rgba(201,164,232,0.7))" },
  matriarchBody: {
    position: "absolute",
    inset: "12% 6%",
    background:
      "radial-gradient(ellipse at 35% 30%, rgba(201,164,232,0.55), rgba(90,70,120,0.75) 70%)",
    borderRadius: "48% 58% 42% 46%",
    border: "1px solid rgba(201,164,232,0.6)",
    boxShadow: "inset -10px -8px 0 rgba(0,0,0,0.35)",
    opacity: 0.9,
  },
  matriarchEyes: {
    position: "absolute",
    left: "22%",
    top: "34%",
    width: 8,
    height: 8,
    borderRadius: "50%",
    background: "#fff",
    boxShadow: "18px 0 0 #fff, 0 0 12px #c9a4e8, 18px 0 12px #c9a4e8",
  },
  cub: { position: "absolute", width: TILE * 0.7, height: TILE * 0.7, zIndex: 8, transition: "left 0.5s linear, top 0.5s linear" },
  cubBody: {
    position: "absolute",
    inset: "12%",
    background: "radial-gradient(ellipse at 40% 30%, rgba(201,164,232,0.6), rgba(110,90,140,0.7))",
    borderRadius: "50% 55% 45% 45%",
    border: "1px solid rgba(201,164,232,0.5)",
  },
  cubHp: {
    position: "absolute",
    left: 0,
    top: -7,
    width: "100%",
    height: 4,
    background: "rgba(0,0,0,0.6)",
    borderRadius: 999,
    overflow: "hidden",
  },
  cubHpFill: { height: "100%", background: "#c9a4e8" },
  prowler: { position: "absolute", width: TILE, height: TILE * 1.2, zIndex: 8, transition: "left 0.16s linear, top 0.16s linear, opacity 0.3s, filter 0.2s" },
  prowlerBody: {
    position: "relative",
    width: "86%",
    height: "60%",
    top: "28%",
    left: "7%",
    background: "rgba(160,140,190,0.5)",
    border: "1px solid rgba(201,164,232,0.6)",
    borderRadius: "45% 55% 40% 40%",
  },
  prowlerStripes: {
    position: "absolute",
    inset: "15% 20%",
    background:
      "repeating-linear-gradient(75deg, transparent 0 6px, rgba(60,45,80,0.55) 6px 9px)",
    borderRadius: "inherit",
  },
  eyes: {
    position: "absolute",
    left: "14%",
    top: "26%",
    width: 5,
    height: 5,
    borderRadius: "50%",
    background: "#fff",
    boxShadow: "10px 0 0 #fff",
  },
  enemyTelegraph: { filter: "drop-shadow(0 0 10px #e86a5c) brightness(1.5)" },
  enemyHp: {
    position: "absolute",
    left: "-8%",
    top: "-14%",
    width: "116%",
    height: 5,
    background: "rgba(0,0,0,0.6)",
    border: "1px solid rgba(201,164,232,0.4)",
    borderRadius: 999,
    overflow: "hidden",
  },
  enemyHpFill: { height: "100%", background: "#c9a4e8", transition: "width 0.12s" },
  bleedTag: {
    position: "absolute",
    left: "-16%",
    bottom: "-26%",
    color: "#e86a5c",
    fontSize: 10,
    whiteSpace: "nowrap",
  },
  downMark: { position: "absolute", color: "#6d5f47", fontSize: 20, zIndex: 7 },
  motes: {
    position: "absolute",
    color: spectral,
    fontSize: 26,
    zIndex: 9,
    textShadow: "0 0 20px rgba(201,164,232,0.8)",
    letterSpacing: "0.4em",
  },
  float: {
    position: "absolute",
    zIndex: 30,
    fontSize: 15,
    fontWeight: 700,
    textShadow: "0 1px 3px #000",
    transform: "translate(-50%, -100%)",
    transition: "top 0.9s ease-out, opacity 0.9s",
    pointerEvents: "none",
  },
  prompt: {
    position: "fixed",
    bottom: 130,
    left: "50%",
    transform: "translateX(-50%)",
    color: spectral,
    fontSize: 13.5,
    background: "rgba(12,10,18,0.85)",
    border: "1px solid rgba(201,164,232,0.5)",
    borderRadius: 999,
    padding: "7px 18px",
    zIndex: 130,
    letterSpacing: "0.04em",
  },
  felt: {
    position: "fixed",
    top: "15%",
    left: "50%",
    transform: "translateX(-50%)",
    color: gold,
    fontSize: 14,
    fontStyle: "italic",
    textShadow: "0 0 16px rgba(232,163,61,0.5)",
    zIndex: 120,
    pointerEvents: "none",
    maxWidth: "88vw",
    textAlign: "center",
  },
  whisper: {
    position: "fixed",
    top: "24%",
    left: "50%",
    transform: "translateX(-50%)",
    color: spectral,
    fontSize: 15,
    fontStyle: "italic",
    letterSpacing: "0.14em",
    textShadow: "0 0 18px rgba(201,164,232,0.7)",
    zIndex: 120,
    pointerEvents: "none",
  },
  victoryLine: {
    color: "#b5a284",
    fontSize: 12.5,
    fontStyle: "italic",
    maxWidth: 560,
    textAlign: "center",
    marginTop: 10,
    lineHeight: 1.6,
  },
  victorySub: { color: spectral },
  downVeil: {
    position: "fixed",
    inset: 0,
    background: "rgba(3,2,6,0.9)",
    zIndex: 300,
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
  },
  downText: {
    color: "#e86a5c",
    fontSize: 26,
    letterSpacing: "0.14em",
    marginBottom: 10,
    textShadow: "0 0 30px rgba(232,106,92,0.5)",
  },
  downSub: { color: "#8a7a5c", fontSize: 13, fontStyle: "italic", maxWidth: 460, textAlign: "center" },
  controls: { color: "#6d5f47", fontSize: 11, marginTop: 8, textAlign: "center" },
  back: {
    background: "transparent",
    border: "1px solid rgba(201,164,232,0.5)",
    color: spectral,
    padding: "9px 20px",
    borderRadius: 8,
    fontSize: 13,
    cursor: "pointer",
    fontFamily: "inherit",
    letterSpacing: "0.05em",
    marginTop: 10,
  },
  interactPad: {
    position: "fixed",
    right: 24,
    bottom: 170,
    width: 56,
    height: 56,
    borderRadius: "50%",
    border: "1px solid rgba(201,164,232,0.7)",
    background: "rgba(201,164,232,0.15)",
    color: spectral,
    fontSize: 20,
    zIndex: 140,
    cursor: "pointer",
  },
};
